import { type CurrencyCode, getCurrencyOptions } from '@vestigas/utils';

import { Select } from './Select';
import { type SelectProps } from './types';

type CurrencySelectProps<T> = Omit<SelectProps<T>, 'options'> & {
  /**
   * Locale for currency name localization.
   * Defaults to the active UI language.
   */
  readonly locale?: string;
  /**
   * Currency codes to prioritize at the top of the list.
   * Defaults to ['EUR', 'USD', 'CHF', 'GBP'].
   */
  readonly prioritizedCurrencies?: CurrencyCode[];
};

/**
 * A select component for selecting currencies.
 * Displays localized currency names.
 * Common currencies (EUR, USD, CHF, GBP) are shown first by default.
 */
export const CurrencySelect = <T,>({
  className,
  errors,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = false,
  locale,
  onChange,
  placeholder = 'Währung auswählen',
  prioritizedCurrencies,
  style,
  testId,
  value,
  ...rest
}: CurrencySelectProps<T>) => {
  const options = getCurrencyOptions(locale, prioritizedCurrencies);

  return (
    <Select
      className={className}
      errors={errors}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMultiSelect={isMultiSelect}
      options={options}
      placeholder={placeholder}
      style={style}
      testId={testId}
      value={value}
      onChange={onChange}
      {...rest}
    />
  );
};
